import React, { useMemo } from 'react'
import { MotifTable } from '@ey-xd/motif-react'
import '@/components/Table/style.scss'
import PaginationContext from '@/contexts/PaginationContext'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'

type TableParams = {
  columnDefs: Array<object>
  cellRenderers: Array<object>
  TitleComplement: React.FC
  title: string
  description: string
  caseRenderers?: object
  pagination?: boolean
  defaultColDef?: any
  getRowStyle?: any
  grandTotalRow?: any
  autoGroupColumnDef?: any
  gridRef?: any
  onGridReady?: any
  pinnedBottomRowData?: any
  overlayNoRowsTemplate?: any
  onRowClicked?: (params: any) => void
  onRowSelected?: (params: any) => void
  onSelectionChanged?: (params: any) => void
  rowSelection?: 'single' | 'multiple'
  columnTypes?: any
  handlePaginationData?: (data: any, columnTypesData: any) => void
  paginationObj?: any
  titleExt?: string
  rowHeight?: number
  onCellValueChanged?: (params: any) => void
  singleClickEdit?: boolean
  suppressClickEdit?: boolean
  stopEditingWhenCellsLoseFocus?: boolean
  enableCellTextSelection?: boolean
  suppressMovableColumns?: boolean
  defaultPageSize?: number
}

export default function Table({
  columnDefs,
  cellRenderers,
  TitleComplement,
  title,
  description,
  caseRenderers,
  defaultColDef,
  pagination,
  grandTotalRow,
  autoGroupColumnDef,
  gridRef,
  onGridReady,
  pinnedBottomRowData,
  overlayNoRowsTemplate,
  onRowClicked,
  onRowSelected,
  onSelectionChanged,
  rowSelection,
  columnTypes,
  handlePaginationData,
  paginationObj,
  titleExt,
  rowHeight,
  onCellValueChanged,
  singleClickEdit,
  suppressClickEdit,
  stopEditingWhenCellsLoseFocus,
  enableCellTextSelection,
  suppressMovableColumns,
  defaultPageSize,
  getRowStyle
}: TableParams) {
  // sets 25 rows per page (default is 100)
  const paginationPageSize = defaultPageSize ?? 25
  // allows the user to select the page size from a predefined list of page sizes
  const paginationPageSizeSelector = [10, 25, 50]

  const contextValue = useMemo(
    () => ({ paginationPageSize }),
    [paginationPageSize],
  )
  function handleMotifSorting(e: any, paginationObj: any) {
    const sortedColumnsLength: any = e?.columns ? e.columns.length : ''
    if (sortedColumnsLength !== '') {
      const coumnName = e?.columns?.[sortedColumnsLength - 1]?.colId
      const sortName = e?.columns?.[sortedColumnsLength - 1]?.sort
      const typeName = e?.columns?.[sortedColumnsLength - 1]?.colDef?.type
      const paginationJson: any = { ...paginationObj }
      const columnTypesCustom: any = {}
      if (typeName) {
        columnTypesCustom[typeName] = { sort: sortName }
      }
      paginationJson.SortOrder = sortName
      paginationJson.SortColumn =
        coumnName?.charAt(0)?.toUpperCase() + coumnName?.slice(1)
      if (!sortName) {
        delete paginationJson.SortOrder
        delete paginationJson.SortColumn
      }
      if (e?.columns[0]?.colDef?.field) {
        if (handlePaginationData) {
          handlePaginationData(paginationJson, columnTypesCustom)
        }
      }
    }
  }
  // this validation will triger only if custom sorting is required
  if (handlePaginationData) {
    if (defaultColDef) {
      defaultColDef = { ...defaultColDef, comparator: () => 0 }
    } else {
      defaultColDef = { comparator: () => 0 }
    }
  }
  let titleName = title
  if (titleExt || titleExt === '') {
    titleName = `${title}, ${titleExt}`
  }

  const optionalTableProps =
    typeof rowHeight === 'number' ? { rowHeight } : {}

  return (
    <PaginationContext.Provider value={contextValue}>
      <MotifTable
        columnTypes={columnTypes ? { ...columnTypes } : {}}
        onSortChanged={(e: any) => {
          if (handlePaginationData) {
            handleMotifSorting(e, paginationObj)
          }
        }}
        columnDefs={columnDefs}
        components={caseRenderers}
        description={description}
        onFilterChanged={function Ga() { }}
        pagination={pagination}
        paginationPageSize={paginationPageSize}
        paginationPageSizeSelector={paginationPageSizeSelector}
        rowData={cellRenderers}
        suppressRowClickSelection
        title={titleName}
        titleComplement={<TitleComplement />}
        defaultColDef={defaultColDef}
        domLayout="autoHeight"
        grandTotalRow={grandTotalRow}
        autoGroupColumnDef={autoGroupColumnDef}
        ref={gridRef}
        onGridReady={onGridReady}
        pinnedBottomRowData={pinnedBottomRowData}
        overlayNoRowsTemplate={overlayNoRowsTemplate}
        onRowClicked={onRowClicked}
        onRowSelected={onRowSelected}
        rowSelection={rowSelection}
        onSelectionChanged={onSelectionChanged}
        onCellValueChanged={onCellValueChanged}
        singleClickEdit={singleClickEdit}
        suppressClickEdit={suppressClickEdit}
        stopEditingWhenCellsLoseFocus={stopEditingWhenCellsLoseFocus}
        enableCellTextSelection={enableCellTextSelection}
        suppressMovableColumns={suppressMovableColumns}
        getRowStyle={getRowStyle}
        {...optionalTableProps}
      />
    </PaginationContext.Provider>
  )
}
